import { format } from 'date-fns';
import Image from 'next/image';
import { getLessonPageUrl } from 'constants/pages';
import { EMPTY_AVATAR_SRC } from 'constants/user';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { nameToLastInitials } from 'utils/shared/name/nameToLastInitials';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import Directions from 'svg/Directions';
import Location from 'svg/Location';
import { ButtonLink } from 'components/Button';
import { ClinicCardProps } from '../types';

const ClinicCard: React.FC<ClinicCardProps> = ({ groupLesson }) => {
  const startDateTime = groupLesson?.startDateTime;
  const lessonDate = startDateTime && format(new Date(startDateTime), 'eeee MMM. d');
  const lessonStartTime = startDateTime && format(new Date(startDateTime), 'p');
  const lessonEndTime = groupLesson?.endDateTime && format(new Date(groupLesson.endDateTime), 'p');
  const participantLimit = groupLesson?.participantLimit || 0;
  const participantCount = groupLesson?.participants.length;

  const spotsLeft = participantLimit - participantCount;

  const price = groupLesson.priceUnitAmount
    ? convertUnitPriceToFormattedPrice(groupLesson.priceUnitAmount, groupLesson.currency)
        .priceDisplay
    : 0;

  const emptyParticipantSlots = new Array(Math.max(0, participantLimit - participantCount)).fill(
    null,
  );

  const renderLocation = () => (
    <div className="inline-flex gap-1 text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
      <Location className="h-4 w-4 shrink-0" />
      <span className="typography-product-caption">
        {groupLesson?.userCustomCourt?.fullAddress || groupLesson?.venue?.addressString}
      </span>
    </div>
  );

  const renderParticipants = () => (
    <div className="mt-4 flex flex-wrap gap-4">
      {groupLesson?.participants.map((participant) => (
        <Participant
          key={participant.id}
          name={nameToLastInitials(participant.userProfile?.fullName || 'Player')}
          imagePath={getProfileImageUrlOrPlaceholder({
            path: participant.userProfile?.profileImagePath || '',
          })}
          isFilled={true}
        />
      ))}
      {emptyParticipantSlots.map((_, index) => (
        <Participant key={index} name="Open" imagePath={EMPTY_AVATAR_SRC} isFilled={false} />
      ))}
    </div>
  );

  return (
    <div className="flex gap-ds-2xl rounded-xl border border-color-border-card-lightmode bg-color-bg-lightmode-primary p-ds-2xl dark:border-color-border-card-darkmode dark:bg-color-bg-darkmode-primary max-sm:flex-col max-sm:gap-ds-xl max-sm:p-ds-xl">
      {/* Lesson Date and Time */}
      <div className="flex flex-1 flex-col">
        <span className="typography-product-body-highlight text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          {lessonDate} <span className="text-color-brand-primary">&bull;</span> {lessonStartTime} -{' '}
          {lessonEndTime}
        </span>

        {/* Lesson Title */}
        <span className="typography-product-heading mt-4 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          {groupLesson?.title}
        </span>

        {/* Location and Directions */}
        <div className="mt-2 flex items-center justify-between gap-1">
          {renderLocation()}
          <a
            target="_blank"
            href={`https://maps.google.com?q=${
              groupLesson?.userCustomCourt?.fullAddress || groupLesson?.venue?.addressString
            }`}
            className="inline-flex items-center gap-1"
          >
            <Directions className="h-4 w-4 shrink-0" />
            <span className="typography-product-button-label-medium">Directions</span>
          </a>
        </div>

        {/* Participants Info */}
        <div className="mt-12">
          <div className="inline-flex items-center gap-4">
            <span className="typography-product-subheading">
              Players going {groupLesson?.participants.length || '0'}/
              {groupLesson?.participantLimit}
            </span>
            <span className="typography-product-caption text-color-text-lightmode-tertiary dark:text-color-bg-darkmode-tertiary">
              {spotsLeft} spots left
            </span>
          </div>

          {renderParticipants()}
        </div>
      </div>

      {/* Divider */}
      <div className="border-[0.5px]" />

      {/* Event Fees and View Details Button */}
      <div className="flex shrink-0 flex-col justify-between max-sm:gap-6">
        <div className="flex gap-4">
          <span className="typography-product-body-highlight text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            Event Fees
          </span>
          <span className="typography-product-subheading">{price}</span>
        </div>

        <div className="flex flex-col gap-ds-lg max-sm:flex-row">
          <ButtonLink href={getLessonPageUrl(groupLesson.id)} variant="secondary" size="md">
            View Details
          </ButtonLink>
        </div>
      </div>
    </div>
  );
};

export default ClinicCard;

const Participant: React.FC<{ name: string; imagePath: string; isFilled: boolean }> = ({
  name,
  imagePath,
  isFilled,
}) => (
  <div className="flex flex-col items-center justify-center gap-2">
    <div className="relative h-8 w-8 rounded">
      <Image
        fill
        src={imagePath}
        alt="avatar"
        className={!isFilled ? 'rounded-full grayscale' : 'rounded-full'}
      />
    </div>
    <span className="typography-product-tabbar-mobile">{name}</span>
  </div>
);
